<template>
  <div>
    <!-- Task Table -->
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
            <th scope="col" class="relative px-6 py-3">
              <span class="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <template v-for="reminder in reminders" :key="reminder.id">
            <tr>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900">{{ reminder.document_name }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-500">{{ reminder.user_name }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                  {{ reminder.action }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm" :class="getDueDateClass(reminder.due_at)">
                  {{ formatDate(reminder.due_at) }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  @click="toggleReminderExpanded(reminder.id)"
                  class="text-primary hover:text-primary-dark transition-transform duration-200"
                  :class="{ 'transform rotate-180': expandedReminderId === reminder.id }"
                >
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
              </td>
            </tr>
            
            <!-- Expanded Actions Section -->
            <tr v-if="expandedReminderId === reminder.id">
              <td colspan="5" class="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div class="flex space-x-3">
                  <!-- Show Send Reminder button only if reminder action is DOWNLOAD -->
                  <button
                    v-if="reminder.action === 'DOWNLOAD'"
                    @click="showEmailReminderModal(reminder.document_id, reminder.user_name, reminder.user_email, reminder.action)"
                    class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                    Send Reminder
                  </button>
                  
                  <!-- Show message when no actions are available -->
                  <div v-else class="text-sm text-gray-500 italic">
                    No actions available
                  </div>
                </div>
              </td>
            </tr>
          </template>
          
          <!-- Empty state when no reminders are available -->
          <tr v-if="reminders.length === 0">
            <td colspan="5" class="px-6 py-10 text-center">
              <p class="text-gray-500">No reminders available</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Email Review Modal -->
    <EmailReviewModal
      :is-open="showEmailModal"
      :process-step-type="'DOWNLOAD'"
      :recipient-email="currentTaskData.email"
      :template-variables="currentTaskData"
      @close="closeEmailModal"
      @send-success="handleEmailSendSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { TaskDocProfile } from '@/features/task-management';
import EmailReviewModal from '../EmailReviewModal.vue';
import { useEmailReminder } from '@/composables/useEmailReminder';

// Props
defineProps<{
  reminders: TaskDocProfile[];
}>();

// State
const expandedReminderId = ref<string | null>(null);

// Toggle expanded reminder
const toggleReminderExpanded = (reminderId: string) => {
  if (expandedReminderId.value === reminderId) {
    expandedReminderId.value = null;
  } else {
    expandedReminderId.value = reminderId;
  }
};

// Use the email reminder composable
const {
  showEmailModal,
  currentTaskData,
  showEmailReminderModal,
  closeEmailModal,
  handleEmailSendSuccess
} = useEmailReminder();

// Format date
const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Get due date class based on date
const getDueDateClass = (dateString: string | null | undefined) => {
  if (!dateString) return 'text-gray-500';
  
  const dueDate = new Date(dateString);
  const today = new Date();
  
  // Reset time part for accurate day calculation
  dueDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  
  if (dueDate < today) {
    return 'text-red-500'; // Overdue
  } else if (dueDate.getTime() === today.getTime()) {
    return 'text-yellow-500'; // Due today
  } else {
    return 'text-gray-500'; // Future
  }
};
</script>